import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { usePowerDialer } from "@/context/PowerDialerContext";
import { useGetCampaignQuery } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import PowerDialerLayout from "@/components/layouts/PowerDialerLayout";
import CampaignSelectScreen from "@/components/pages/power-dialer/CampaignSelectScreen";

export default function PowerDialerPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { isActive, activate, deactivate } = usePowerDialer();

  const { data: campaign, isLoading, error } = useGetCampaignQuery(campaignId!, { skip: !campaignId });

  useEffect(() => {
    if (campaign && !isActive) {
      activate(campaign);
    }
  }, [campaign, isActive, activate]);

  useEffect(() => {
    return () => {
      deactivate();
    };
  }, [deactivate]);

  // No campaign ID — show selection screen
  if (!campaignId) {
    return <CampaignSelectScreen />;
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-destructive">{apiErrorMessage(error, "Failed to load campaign")}</p>
          <button className="text-sm text-primary underline" onClick={() => navigate("/power-dialer")}>
            Back to campaign selection
          </button>
        </div>
      </div>
    );
  }

  if (!isActive) {
    return null;
  }

  return <PowerDialerLayout />;
}
